import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Injector,
  NgZone,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import moment, { Moment } from 'moment';
import { SharedModule } from '@shared/shared.module';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { downloadBlob, downloadText } from '@shared/utils/download-file.utils';
import { printWithFlowingLayout } from '@shared/utils/print.utils';
import { DateRange, isDateRange } from '@shared/components/atoms/inputs/e-date-picker/e-date-picker.component';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdPlanetInfoService } from '../../chart/bodygraph/hd-planet-info.service';
import { HdGateInfoService } from '../../chart/bodygraph/hd-gate-info.service';
import { HD_ZODIAC_SIGN_ORDER, HdZodiacSign, hdZodiacPositionFromLongitude } from '../../chart/bodygraph/hd-gate-coordinates';
import {
  formatHdAbsoluteDegrees,
  formatHdZodiacDegrees,
  hdAbsoluteDegreesFormat,
} from '../../chart/bodygraph/gate-info/variants/hd-zodiac-position.util';
import { findHdChartPointDisplay } from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { activationFromLongitude } from '../../chart/mandala/mandala-layout';
import { HdChartSettingsStore } from '../../chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { IChingLinePosition } from '../../iching/core/iching.models';
import { HdIChingAccessService } from '../../iching/hd-iching-access.service';
import { HdIChingPanelService } from '../../iching/panel/hd-iching-panel.service';
import { EphemerisNotation } from '../ephemeris-daily.utils';
import { EphemerisDisplay, EphemerisDisplayColumn, EphemerisDisplayRow, buildEphemerisDisplay } from '../ephemeris-display.utils';
import { EphemerisExportSheet, buildEphemerisCsv, buildEphemerisExportSheet } from '../ephemeris-export.utils';
import { EphemerisTableDataService } from '../ephemeris-table-data.service';
import {
  EPHEMERIS_BODY_KEYS,
  EPHEMERIS_CHART_POINT_KEYS,
  EPHEMERIS_UNICODE_GLYPHS,
  EphemerisBodyKey,
  EphemerisMoment,
} from '../ephemeris.models';
import {
  EphemerisMode,
  EphemerisViewPrefs,
  loadEphemerisViewPrefs,
  saveEphemerisViewPrefs,
} from '../ephemeris-view-prefs';
import { ephemerisMomentKey, loadInsertedMoments, saveInsertedMoments } from '../ephemeris-inserted-moments';
import {
  EPHEMERIS_INSERT_TIME_FORMAT,
  EphemerisInsertWindow,
  ephemerisInsertDays,
  ephemerisInsertNeedsDay,
  resolveEphemerisInsertMoment,
} from '../ephemeris-insert-time.utils';

const I18N_PREFIX = 'humanDesign.ephemeris';

/** Standaardvenster: een week terug en een week vooruit rond vandaag (wens Steven). */
const DEFAULT_DAYS_BACK = 7;
const DEFAULT_DAYS_FORWARD = 7;

/** Grenzen vóór de eerste response (spiegelen EphemerisCoverage in de backend). */
const FALLBACK_BOUNDS = {
  minUtc: moment.utc('1802-01-01T00:00:00Z'),
  maxUtc: moment.utc('2398-01-01T00:00:00Z'),
  maxWindowDays: 31,
};

/** Hoe vaak "nu" opschuift (de Maan wisselt ~elke 2 uur van lijn; per minuut volstaat). */
const NOW_TICK_MS = 60_000;


/** Het opgevraagde venster: UTC-instants, `to` exclusief. */
interface EphemerisWindow {
  readonly from: Moment;
  readonly to: Moment;
}

interface EphemerisColumnVm {
  readonly key: EphemerisBodyKey;
  readonly glyph: string;
  readonly color: string;
  readonly name: string;
}

/** Een tabelkolom: lichaam + weergave (Maan +6u krijgt een badge). */
interface EphemerisTableColumnVm extends EphemerisColumnVm {
  readonly colKey: string;
  readonly moonLater: boolean;
  /** Tooltip van de kolomkop (naam of uitleg +6u, plus de markeerhint). */
  readonly title: string;
}

interface EphemerisColumnOption {
  readonly key: EphemerisBodyKey;
  readonly glyph: string;
  readonly color: string;
  readonly name: string;
  readonly visible: boolean;
}

/**
 * Open invoegveld tussen twee rijen. Enkel de tijd is te kiezen (wens Steven 2026-10-13): de dag
 * volgt uit het venster. Alleen bij een venster van een dag of langer komt er een dagkeuze bij,
 * beperkt tot de dagen binnen het venster.
 */
interface EphemerisInsertDraft {
  /** De rij waarna ingevoegd wordt. */
  readonly afterId: string;
  readonly window: EphemerisInsertWindow;
  readonly days: readonly string[];
  readonly needsDay: boolean;
  /** Gekozen dag (`YYYY-MM-DD`), enkel gebruikt als `needsDay`. */
  readonly day: string;
  /** `HH:mm:ss` (of `HH:mm`) in de weergavetijdzone. */
  readonly time: string;
}

/**
 * Ephemeris in tabelvorm (spec `docs/superpowers/specs/2026-10-13-ephemeris-pagina-design.md`).
 * Twee weergaven op dezelfde tabel: Wissels (elke lijn-/poortwissel en elk station, met de
 * volledige stand ná dat moment) en Dagposities (00:00 en 12:00, de Maan ook 6 uur later).
 *
 * Wat de gebruiker ziet is één weergavelijst ({@link buildEphemerisDisplay}); de tabel, de
 * afdruk en de export (CSV/Excel) gebruiken die allemaal, dus wat je ziet is wat je krijgt.
 * Daarbovenop: zelf rijen tussenvoegen op een gekozen tijdstip (enkel deze sessie), kolommen,
 * rijen en cellen markeren om te volgen, en een tooltip met de andere notaties per cel.
 */
@Component({
  selector: 'hd-ephemeris-table',
  imports: [SharedModule, ESegmentedToggleComponent, NgSelectModule],
  providers: [EphemerisTableDataService],
  templateUrl: './hd-ephemeris-table.component.html',
  styleUrl: './hd-ephemeris-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdEphemerisTableComponent {
  private readonly data = inject(EphemerisTableDataService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly injector = inject(Injector);
  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly ichingPanel = inject(HdIChingPanelService);
  private readonly settings = inject(HdChartSettingsStore);

  /** Classic I Ching-icoon per rij (feature + setting + gebiedssetting transits). */
  protected readonly ichingIconsVisible = computed(
    () => this.ichingAccess.classicVisible() && this.settings.classicIChingIconsTransits(),
  );

  /** Hexagram van de dag van deze rij in het Classic I Ching-paneel (zon en maan op het rijmoment). */
  protected openIChingDay(row: EphemerisDisplayRow, event: MouseEvent): void {
    const iching = row.iching;
    if (!iching) {
      return;
    }
    this.ichingPanel.openDay(
      {
        view: 'day',
        dateKey: row.at.format('YYYY-MM-DD'),
        sun: { gate: iching.sun.gate, line: iching.sun.line as IChingLinePosition },
        moon: { gate: iching.moon.gate, line: iching.moon.line as IChingLinePosition },
        momentLabel: row.at.clone().locale(momentLocaleFor(this.lang())).format('D MMM YYYY, HH:mm'),
      },
      { x: event.clientX, y: event.clientY },
    );
  }

  private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private readonly periodHost = viewChild<ElementRef<HTMLElement>>('periodHost');
  private readonly columnsHost = viewChild<ElementRef<HTMLElement>>('columnsHost');
  private readonly exportHost = viewChild<ElementRef<HTMLElement>>('exportHost');
  private readonly insertInput = viewChild<ElementRef<HTMLInputElement>>('insertInput');

  protected readonly lang = computed(() => {
    // Meelezen zodat vertalingen ná een async scope-load opnieuw evalueren.
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  private readonly prefs = signal<EphemerisViewPrefs>(loadEphemerisViewPrefs());
  protected readonly useUtc = computed(() => this.prefs().useUtc);
  protected readonly mode = computed<EphemerisMode>(() => this.prefs().mode);
  protected readonly notation = computed<EphemerisNotation>(() => this.prefs().notation);
  private readonly hiddenBodies = computed(() => new Set<EphemerisBodyKey>(this.prefs().hiddenBodies));

  protected readonly now = signal<Moment>(moment.utc());
  protected readonly period = signal<EphemerisWindow>(defaultWindow(moment.utc(), this.prefs().useUtc));

  protected readonly loading = this.data.loading;
  protected readonly error = this.data.error;
  protected readonly errorMessage = this.data.errorMessage;

  protected readonly periodOpen = signal(false);
  protected readonly columnsOpen = signal(false);
  protected readonly exportOpen = signal(false);
  /** Excel wordt opgebouwd (ExcelJS lui laden + werkmap schrijven). */
  protected readonly exporting = signal(false);
  /** Gekozen periode was langer dan toegelaten en is ingekort. */
  protected readonly rangeClamped = signal(false);

  /** Door de gebruiker ingevoegde momenten (ISO-UTC), enkel deze sessie. */
  private readonly insertedKeys = signal<readonly string[]>(loadInsertedMoments());
  protected readonly insertDraft = signal<EphemerisInsertDraft | null>(null);
  /** Het moment bij de ingevulde tijd, `undefined` zolang die buiten het venster valt (validatie vóór het invoegen). */
  protected readonly insertCandidate = computed(() => {
    const draft = this.insertDraft();
    return draft ? resolveEphemerisInsertMoment(draft.window, draft.time, draft.needsDay ? draft.day : undefined) : undefined;
  });
  /** Zelfde lijst zolang hetzelfde venster open is (de tijd typen verandert de dagen niet). */
  private readonly insertDays = computed(() => this.insertDraft()?.days ?? [], { equal: (a, b) => a === b });
  /** Dagkeuze bij een venster van een dag of langer: enkel de dagen binnen het venster. */
  protected readonly insertDayOptions = computed(() => {
    const locale = momentLocaleFor(this.lang());
    return this.insertDays().map(day => ({ value: day, label: moment(day, 'YYYY-MM-DD').locale(locale).format('dd D MMM YYYY') }));
  });

  /** Gemarkeerde kolommen (kolomkop aanklikken; opnieuw klikken haalt de markering weg). */
  protected readonly selectedColumns = signal<ReadonlySet<string>>(new Set());
  /** Vastgezette rij (tijdcel aanklikken, of via een cel). */
  protected readonly pinnedRowId = signal<string | null>(null);
  /** Gemarkeerde cel (los van de kolom- en rijmarkering). */
  protected readonly selectedCell = signal<{ readonly rowId: string; readonly colKey: string } | null>(null);

  /** Scrollactie na de volgende tabel: naar de nu-markering (start, "Nu") of naar boven (bladeren, periode). */
  private pendingScroll: 'now' | 'top' | undefined = 'now';

  protected readonly bounds = computed(() => {
    const source = this.data.table() ?? this.data.daily();
    return source
      ? { minUtc: source.minUtc, maxUtc: source.maxUtc, maxWindowDays: source.maxWindowDays }
      : FALLBACK_BOUNDS;
  });

  /**
   * BELANGRIJK (prestatie): vertalingen uit HD-scopes ENKEL hier, één keer per taal.
   * Transloco's `getTranslation(scope)` doorloopt bij elke aanroep de volledige vertaaltabel
   * (`getObjectByKey`, >11.000 sleutels). Per cel aanroepen (de tooltips: ~3500 cellen) maakte
   * elke toggle seconden traag en blokkeerde het tekenen (feedback Steven 2026-10-13).
   * Niet terug per cel of per kolom vertalen; zie de spec "vertaalt HD-scopes niet per cel".
   */
  private readonly zodiacLabels = computed<Readonly<Record<HdZodiacSign, string>>>(() => {
    const lang = this.lang();
    return Object.fromEntries(
      HD_ZODIAC_SIGN_ORDER.map(sign => [sign, this.gateInfo.zodiacLabelIn(sign, lang)]),
    ) as Record<HdZodiacSign, string>;
  });

  /** Glyph, kleur en naam per lichaam, één keer per taal (zie {@link zodiacLabels}). */
  private readonly columnsByKey = computed<Readonly<Record<EphemerisBodyKey, EphemerisColumnVm>>>(() => {
    const planetNames = this.planetInfo.planetTranslationsAllIn(this.lang());
    return Object.fromEntries(
      EPHEMERIS_BODY_KEYS.map(key => {
        const chartPointKey = EPHEMERIS_CHART_POINT_KEYS[key];
        const display = findHdChartPointDisplay(chartPointKey);
        const name = planetNames[chartPointKey]?.name || display?.label || key;
        return [key, { key, glyph: display?.astronomiconChar ?? '', color: display?.stripeColor ?? '', name }];
      }),
    ) as Record<EphemerisBodyKey, EphemerisColumnVm>;
  });

  /** Tooltip van het ℞-teken: één vertaling i.p.v. één per retrograde cel per change detection. */
  protected readonly retrogradeLabel = computed(() => this.t('retrograde'));

  protected readonly minYear = computed(() => this.bounds().minUtc.year());
  protected readonly maxYear = computed(() => this.bounds().maxUtc.clone().subtract(1, 'ms').year());

  /** De geladen ingevoegde momenten (die binnen de periode worden getoond). */
  private readonly insertedMoments = computed<readonly EphemerisMoment[]>(() => {
    const loaded = this.data.moments();
    return this.insertedKeys()
      .map(key => loaded.get(key))
      .filter((value): value is EphemerisMoment => value !== undefined);
  });

  /** Wat je ziet: tabel, afdruk en export gebruiken deze ene lijst. */
  protected readonly display = computed<EphemerisDisplay | undefined>(() => {
    const lang = this.lang();
    return buildEphemerisDisplay({
      mode: this.mode(),
      notation: this.notation(),
      useUtc: this.useUtc(),
      lang,
      now: this.now(),
      hidden: this.hiddenBodies(),
      table: this.data.table(),
      daily: this.data.daily(),
      inserted: this.insertedMoments(),
      tooltipFor: this.positionTooltipFormatter(lang),
      texts: {
        begin: this.t('begin'),
        stationRetrograde: this.t('stationRetrograde'),
        stationDirect: this.t('stationDirect'),
      },
    });
  });

  protected readonly columns = computed<readonly EphemerisTableColumnVm[]>(() => {
    const byKey = this.columnsByKey();
    const selectHint = this.t('selectColumnHint');
    const moonLaterHint = this.t('moonLaterHint');
    return (this.display()?.columns ?? []).map((column: EphemerisDisplayColumn) => ({
      ...byKey[column.key],
      colKey: column.colKey,
      moonLater: column.moonLater,
      title: `${column.moonLater ? moonLaterHint : byKey[column.key].name} · ${selectHint}`,
    }));
  });

  /** Per zichtbare kolom: gemarkeerd? (index = kolomindex, goedkoop in de template). */
  protected readonly columnSelected = computed<readonly boolean[]>(() => {
    const selected = this.selectedColumns();
    return this.columns().map(column => selected.has(column.colKey));
  });

  protected readonly columnOptions = computed<readonly EphemerisColumnOption[]>(() => {
    const hidden = this.hiddenBodies();
    const columns = this.columnsByKey();
    return EPHEMERIS_BODY_KEYS.map(key => ({ ...columns[key], visible: !hidden.has(key) }));
  });

  protected readonly nowLabel = computed(() => this.inZone(this.now()).format('HH:mm'));

  protected readonly periodLabel = computed(() => {
    const { from, to } = this.period();
    const lang = this.lang();
    const first = this.inZone(from).locale(momentLocaleFor(lang)).format('D MMM YYYY');
    const last = this.inZone(to.clone().subtract(1, 'ms')).locale(momentLocaleFor(lang)).format('D MMM YYYY');
    return `${first} – ${last}`;
  });

  /** De periode als kalenderdagen voor de datumpicker (die in lokale datums werkt). */
  protected readonly pickerValue = computed<DateRange>(() => {
    const { from, to } = this.period();
    return {
      from: moment(this.inZone(from).format('YYYY-MM-DD'), 'YYYY-MM-DD'),
      to: moment(this.inZone(to.clone().subtract(1, 'ms')).format('YYYY-MM-DD'), 'YYYY-MM-DD'),
    };
  });

  protected readonly canGoBack = computed(() => this.period().from.isAfter(this.bounds().minUtc));
  protected readonly canGoForward = computed(() => this.period().to.isBefore(this.bounds().maxUtc));

  protected readonly zoneLabel = computed(() =>
    this.useUtc() ? 'UT' : (Intl.DateTimeFormat().resolvedOptions().timeZone ?? this.t('timezoneLocal')),
  );

  /** De gekozen opties in woorden: kop van de afdruk en van de export. */
  protected readonly optionsSummary = computed(() => {
    const parts = [
      this.t(this.mode() === 'daily' ? 'modeDaily' : 'modeChanges'),
      ...(this.mode() === 'daily' ? [this.t(this.notation() === 'zodiac' ? 'notationZodiac' : 'notationDegrees')] : []),
      this.useUtc() ? 'UT' : `${this.t('timezoneLocal')} (${this.zoneLabel()})`,
      this.periodLabel(),
    ];
    return parts.join(' · ');
  });

  /** Knopgroepen van de toolbar (zelfde segmented toggle als de infopanelen en transits). */
  protected readonly modeOptions = computed<readonly ESegmentedToggleOption[]>(() => {
    this.lang();
    return [
      { value: 'changes', label: this.t('modeChanges'), title: this.t('modeChangesHint') },
      { value: 'daily', label: this.t('modeDaily'), title: this.t('modeDailyHint') },
    ];
  });

  protected readonly notationOptions = computed<readonly ESegmentedToggleOption[]>(() => {
    this.lang();
    return [
      { value: 'degrees', label: this.t('notationDegrees'), title: this.t('notationDegreesHint') },
      { value: 'zodiac', label: this.t('notationZodiac'), title: this.t('notationZodiacHint') },
    ];
  });

  protected readonly zoneOptions = computed<readonly ESegmentedToggleOption[]>(() => {
    this.lang();
    return [
      { value: 'local', label: this.t('timezoneLocal') },
      { value: 'utc', label: 'UT' },
    ];
  });

  protected readonly coverageLabel = computed(() => {
    const bounds = this.bounds();
    return this.t('coverage', {
      from: bounds.minUtc.year(),
      to: bounds.maxUtc.clone().subtract(1, 'ms').year(),
      days: bounds.maxWindowDays,
    });
  });

  constructor() {
    // Buiten de zone (zelfde patroon als TransitViewTimeService): de signal-write
    // markeert deze OnPush-component zelf, zonder app-brede change detection per tick.
    const destroyRef = inject(DestroyRef);
    const timer = inject(NgZone).runOutsideAngular(() =>
      setInterval(() => this.now.set(moment.utc()), NOW_TICK_MS),
    );
    destroyRef.onDestroy(() => clearInterval(timer));

    effect(() => {
      const { from, to } = this.period();
      // Dagposities worden in de gekozen tijdzone berekend (00:00 en 12:00 lokaal of UT);
      // bij de wissels verschuift de tijdzone enkel de weergave, dus geen nieuwe opvraging.
      const timeZone = this.mode() === 'daily' ? this.dailyTimeZone() : undefined;
      untracked(() => void (timeZone ? this.data.loadDaily(from, to, timeZone) : this.data.load(from, to)));
    });

    effect(() => {
      // Ingevoegde momenten binnen de periode laden (één opvraging per moment, gecachet).
      this.period();
      this.insertedKeys();
      untracked(() => this.loadInsertedInPeriod());
    });

    effect(() => {
      // Na een nieuwe tabel eenmalig de gevraagde scrollactie; kolommen, tijdzone of
      // notatie wisselen laat de scrollpositie met rust.
      const hasData = !!this.display();
      const pending = this.pendingScroll;
      if (!hasData || !pending) return;
      this.pendingScroll = undefined;
      afterNextRender(() => (pending === 'now' ? this.scrollToNow() : this.scrollToTop()), { injector: this.injector });
    });
  }

  @HostListener('document:pointerdown', ['$event'])
  protected onDocumentPointerDown(event: PointerEvent): void {
    const target = event.target as Node | null;
    if (this.periodOpen() && !this.periodHost()?.nativeElement.contains(target)) {
      this.periodOpen.set(false);
    }
    if (this.columnsOpen() && !this.columnsHost()?.nativeElement.contains(target)) {
      this.columnsOpen.set(false);
    }
    if (this.exportOpen() && !this.exportHost()?.nativeElement.contains(target)) {
      this.exportOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.periodOpen.set(false);
    this.columnsOpen.set(false);
    this.exportOpen.set(false);
    this.insertDraft.set(null);
  }

  protected togglePeriod(): void {
    this.columnsOpen.set(false);
    this.exportOpen.set(false);
    this.periodOpen.update(open => !open);
  }

  protected toggleColumns(): void {
    this.periodOpen.set(false);
    this.exportOpen.set(false);
    this.columnsOpen.update(open => !open);
  }

  protected toggleExport(): void {
    this.periodOpen.set(false);
    this.columnsOpen.set(false);
    this.exportOpen.update(open => !open);
  }

  protected goToNow(): void {
    this.rangeClamped.set(false);
    const now = moment.utc();
    this.now.set(now);
    const target = defaultWindow(now, this.useUtc());
    if (sameWindow(target, this.period())) {
      this.scrollToNow();
      return;
    }
    this.pendingScroll = 'now';
    this.period.set(target);
  }

  protected shift(direction: -1 | 1): void {
    this.rangeClamped.set(false);
    const { from, to } = this.period();
    const length = to.diff(from);
    this.setWindow(from.clone().add(direction * length, 'ms'), to.clone().add(direction * length, 'ms'));
  }

  protected onRangePicked(value: Moment | DateRange | undefined): void {
    if (!isDateRange(value)) return;
    const firstDay = value.from.format('YYYY-MM-DD');
    const lastDay = value.to.format('YYYY-MM-DD');
    const from = this.useUtc() ? moment.utc(firstDay, 'YYYY-MM-DD') : moment(firstDay, 'YYYY-MM-DD');
    const to = (this.useUtc() ? moment.utc(lastDay, 'YYYY-MM-DD') : moment(lastDay, 'YYYY-MM-DD')).add(1, 'day');

    const maxDays = this.bounds().maxWindowDays;
    const tooLong = to.diff(from, 'days', true) > maxDays;
    this.rangeClamped.set(tooLong);
    this.setWindow(from, tooLong ? from.clone().add(maxDays, 'days') : to);
    this.periodOpen.set(false);
  }

  protected onZoneChange(value: string): void {
    this.updatePrefs({ ...this.prefs(), useUtc: value === 'utc' });
  }

  protected onModeChange(value: string): void {
    const mode: EphemerisMode = value === 'daily' ? 'daily' : 'changes';
    if (mode === this.mode()) return;
    this.pendingScroll = 'now';
    this.insertDraft.set(null);
    this.updatePrefs({ ...this.prefs(), mode });
  }

  protected onNotationChange(value: string): void {
    this.updatePrefs({ ...this.prefs(), notation: value === 'zodiac' ? 'zodiac' : 'degrees' });
  }

  protected toggleBody(key: EphemerisBodyKey): void {
    const hidden = new Set(this.prefs().hiddenBodies);
    if (hidden.has(key)) {
      hidden.delete(key);
    } else if (hidden.size < EPHEMERIS_BODY_KEYS.length - 1) {
      // Minstens één kolom blijft zichtbaar.
      hidden.add(key);
    }
    this.updatePrefs({ ...this.prefs(), hiddenBodies: EPHEMERIS_BODY_KEYS.filter(body => hidden.has(body)) });
  }

  protected showAllBodies(): void {
    this.updatePrefs({ ...this.prefs(), hiddenBodies: [] });
  }

  protected retry(): void {
    const { from, to } = this.period();
    void (this.mode() === 'daily' ? this.data.loadDaily(from, to, this.dailyTimeZone()) : this.data.load(from, to));
    // Ook een ingevoegd moment dat mislukte (bv. 429) opnieuw proberen.
    this.loadInsertedInPeriod();
  }

  // ── Markeren: kolommen, rij, cel ─────────────────────────────────────────

  protected toggleColumn(colKey: string): void {
    this.selectedColumns.update(current => {
      const next = new Set(current);
      if (next.has(colKey)) next.delete(colKey);
      else next.add(colKey);
      return next;
    });
  }

  /** Tijdcel aangeklikt: de rij vastzetten (de hover-kleur blijft) of weer losmaken. */
  protected togglePinnedRow(rowId: string): void {
    this.pinnedRowId.update(current => (current === rowId ? null : rowId));
  }

  /** Cel aangeklikt: cel markeren en haar rij vastzetten; nogmaals klikken haalt beide weg. */
  protected toggleCell(rowId: string, colKey: string): void {
    const current = this.selectedCell();
    if (current?.rowId === rowId && current.colKey === colKey) {
      this.selectedCell.set(null);
      if (this.pinnedRowId() === rowId) this.pinnedRowId.set(null);
      return;
    }
    this.selectedCell.set({ rowId, colKey });
    this.pinnedRowId.set(rowId);
  }

  protected isSelectedCell(rowId: string, colKey: string): boolean {
    const current = this.selectedCell();
    return current !== null && current.rowId === rowId && current.colKey === colKey;
  }

  // ── Rijen tussenvoegen (enkel deze sessie) ───────────────────────────────

  /** Invoegveld openen tussen rij `index` en de volgende rij (of het einde van de periode). */
  protected openInsert(index: number): void {
    const display = this.display();
    if (!display) return;
    const row = display.rows[index];
    const window: EphemerisInsertWindow = {
      min: row.atUtc,
      max: display.rows[index + 1]?.atUtc ?? display.toUtc,
      useUtc: this.useUtc(),
    };
    const { min, max } = window;
    const middle = moment.utc(min.valueOf() + Math.floor((max.valueOf() - min.valueOf()) / 2));
    // Op de minuut afgerond voorstellen, tenzij de rijen daarvoor te dicht bij elkaar liggen.
    const rounded = middle.clone().startOf('minute');
    const suggestion = this.inZone(rounded.isAfter(min) ? rounded : middle.clone().startOf('second'));
    this.insertDraft.set({
      afterId: row.id,
      window,
      days: ephemerisInsertDays(window),
      needsDay: ephemerisInsertNeedsDay(window),
      day: suggestion.format('YYYY-MM-DD'),
      time: suggestion.format(EPHEMERIS_INSERT_TIME_FORMAT),
    });
    afterNextRender(() => this.insertInput()?.nativeElement.focus(), { injector: this.injector });
  }

  /**
   * Grenzen voor het tijdveld (`min` > `max` = een venster over middernacht; dat kent `type="time"`).
   * De echte controle is {@link insertCandidate}: typen buiten het venster blijft mogelijk, maar
   * dan staat de fout er meteen en is Invoegen uitgeschakeld.
   */
  protected insertTimeBounds(draft: EphemerisInsertDraft): { readonly min: string | null; readonly max: string | null } {
    const first = draft.needsDay ? draft.day === draft.days[0] : true;
    const last = draft.needsDay ? draft.day === draft.days[draft.days.length - 1] : true;
    return {
      min: first ? this.inZone(draft.window.min).format(EPHEMERIS_INSERT_TIME_FORMAT) : null,
      max: last ? this.inZone(draft.window.max).format(EPHEMERIS_INSERT_TIME_FORMAT) : null,
    };
  }

  protected insertRangeLabel(draft: EphemerisInsertDraft): string {
    const format = 'D MMM HH:mm:ss';
    const locale = momentLocaleFor(this.lang());
    return this.t('insertBetween', {
      from: this.inZone(draft.window.min).locale(locale).format(format),
      to: this.inZone(draft.window.max).locale(locale).format(format),
    });
  }

  /** De dag waarop de ingevulde tijd valt (venster korter dan een dag); leeg zolang de tijd buiten het venster valt. */
  protected insertDateLabel(): string {
    const candidate = this.insertCandidate();
    return candidate ? this.inZone(candidate).locale(momentLocaleFor(this.lang())).format('dd D MMM') : '';
  }


  protected onInsertTime(time: string): void {
    this.insertDraft.update(draft => (draft ? { ...draft, time } : draft));
  }

  protected onInsertDay(day: string): void {
    this.insertDraft.update(draft => (draft ? { ...draft, day } : draft));
  }

  protected confirmInsert(): void {
    const candidate = this.insertCandidate();
    if (!candidate) return;
    const key = ephemerisMomentKey(candidate);
    if (!this.insertedKeys().includes(key)) {
      this.setInsertedKeys([...this.insertedKeys(), key]);
    }
    this.insertDraft.set(null);
  }

  protected cancelInsert(): void {
    this.insertDraft.set(null);
  }

  protected removeInserted(row: EphemerisDisplayRow): void {
    const key = ephemerisMomentKey(row.atUtc);
    this.setInsertedKeys(this.insertedKeys().filter(existing => existing !== key));
    if (this.pinnedRowId() === row.id) this.pinnedRowId.set(null);
    if (this.selectedCell()?.rowId === row.id) this.selectedCell.set(null);
  }

  // ── Afdrukken en exporteren ──────────────────────────────────────────────

  /** Afdrukken via de browser (liggend, zie de print-stijlen), met de gekozen opties als kop. */
  protected print(): void {
    this.exportOpen.set(false);
    printWithFlowingLayout();
  }

  protected exportCsv(): void {
    const sheet = this.exportSheet();
    if (!sheet) return;
    this.exportOpen.set(false);
    downloadText(buildEphemerisCsv(sheet), `${sheet.fileBaseName}.csv`, 'text/csv');
  }

  protected async exportExcel(): Promise<void> {
    const sheet = this.exportSheet();
    if (!sheet || this.exporting()) return;
    this.exportOpen.set(false);
    this.exporting.set(true);
    try {
      // Lui geladen: ExcelJS zit niet in de gewone bundel van de app.
      const { buildEphemerisXlsx } = await import('../ephemeris-excel');
      downloadBlob(await buildEphemerisXlsx(sheet), `${sheet.fileBaseName}.xlsx`);
    } finally {
      this.exporting.set(false);
    }
  }

  private exportSheet(): EphemerisExportSheet | undefined {
    const display = this.display();
    if (!display) return undefined;
    const columns = this.columns().map(
      column => `${EPHEMERIS_UNICODE_GLYPHS[column.key]} ${column.name}${column.moonLater ? ` ${this.t('moonLater')}` : ''}`,
    );
    const { from, to } = this.period();
    const fileBaseName = [
      'ephemeris',
      this.mode() === 'daily' ? 'dagposities' : 'wissels',
      ...(this.mode() === 'daily' ? [this.notation() === 'zodiac' ? 'zodiac' : 'graden'] : []),
      this.useUtc() ? 'ut' : 'lokaal',
      this.inZone(from).format('YYYY-MM-DD'),
      this.inZone(to.clone().subtract(1, 'ms')).format('YYYY-MM-DD'),
    ].join('_');
    return buildEphemerisExportSheet(
      display,
      {
        title: this.t('title'),
        options: this.optionsSummary(),
        date: this.t('exportDate', { zone: this.useUtc() ? 'UT' : this.zoneLabel() }),
        time: this.t('exportTime', { zone: this.useUtc() ? 'UT' : this.zoneLabel() }),
        rowType: this.t('exportRowType'),
        rowTypes: {
          initial: this.t('begin'),
          state: this.t(this.mode() === 'daily' ? 'exportRowDaily' : 'exportRowChange'),
          inserted: this.t('exportRowInserted'),
        },
        columns,
      },
      this.lang(),
      fileBaseName,
    );
  }

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`${I18N_PREFIX}.${key}`, params, this.lang());
  }

  /** IANA-tijdzone van de dagposities: UT of die van de browser. */
  private dailyTimeZone(): string {
    return this.useUtc() ? 'UTC' : (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  }

  /**
   * Tooltip voor een positie: `11.6.1.1.1 · 267,3125° · 27°18'45" Boogschutter`, zodat je in
   * elke weergave de andere notaties ziet (wens Steven 2026-10-13).
   */
  private positionTooltipFormatter(lang: string): (longitude: number | undefined) => string {
    const numberFormat = hdAbsoluteDegreesFormat(lang);
    const zodiacLabels = this.zodiacLabels();
    return longitude => {
      if (longitude === undefined) return '';
      const activation = activationFromLongitude(longitude);
      const zodiac = hdZodiacPositionFromLongitude(longitude);
      return [
        `${activation.gate}.${activation.line}.${activation.color}.${activation.tone}.${activation.base}`,
        formatHdAbsoluteDegrees(longitude, numberFormat),
        `${formatHdZodiacDegrees(zodiac)} ${zodiacLabels[zodiac.zodiacSign]}`,
      ].join(' · ');
    };
  }

  private loadInsertedInPeriod(): void {
    const { from, to } = this.period();
    for (const key of this.insertedKeys()) {
      const at = moment.utc(key);
      if (!at.isBefore(from) && at.isBefore(to)) void this.data.loadMoment(at);
    }
  }

  private setInsertedKeys(keys: readonly string[]): void {
    this.insertedKeys.set(keys);
    saveInsertedMoments(keys);
  }

  private updatePrefs(prefs: EphemerisViewPrefs): void {
    this.prefs.set(prefs);
    saveEphemerisViewPrefs(prefs);
  }

  /** Zet het venster, geklemd binnen de dekking (de lengte blijft behouden). */
  private setWindow(from: Moment, to: Moment): void {
    const { minUtc, maxUtc } = this.bounds();
    let start = from.clone().utc();
    let end = to.clone().utc();
    const length = end.diff(start);
    if (start.isBefore(minUtc)) {
      start = minUtc.clone();
      end = start.clone().add(length, 'ms');
    }
    if (end.isAfter(maxUtc)) {
      end = maxUtc.clone();
      start = end.clone().subtract(length, 'ms');
    }
    const next = { from: start, to: end };
    if (sameWindow(next, this.period())) return;
    this.pendingScroll = 'top';
    this.insertDraft.set(null);
    this.period.set(next);
  }

  private scrollToTop(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (container) container.scrollTop = 0;
  }

  private scrollToNow(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) return;
    const marker = container.querySelector<HTMLElement>('[data-now-anchor]');
    if (!marker) {
      container.scrollTop = 0;
      return;
    }
    const offset = marker.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTop += offset - container.clientHeight / 2;
  }

  private inZone(utc: Moment): Moment {
    return this.useUtc() ? utc.clone().utc() : utc.clone().local();
  }
}

function defaultWindow(now: Moment, useUtc: boolean): EphemerisWindow {
  const today = (useUtc ? now.clone().utc() : now.clone().local()).startOf('day');
  return {
    from: today.clone().subtract(DEFAULT_DAYS_BACK, 'days').utc(),
    to: today.clone().add(DEFAULT_DAYS_FORWARD + 1, 'days').utc(),
  };
}

function sameWindow(a: EphemerisWindow, b: EphemerisWindow): boolean {
  return a.from.isSame(b.from) && a.to.isSame(b.to);
}
